import { StepType } from '@reactour/tour';
import TourStepContent from '../TourStepContent';
import { TourConfig } from '../tourTypes';
import { blockAllNoDim, blockInteraction } from '../stepStyles';

// Step indices for the two CANmeet tours. CREATE (open dialog) is a gate for
// both; MODE (click Schedule) is an extra gate for the scheduled tour. Driven by
// useCanmeetTourSync.
export const CANMEET_INSTANT_STEP = {
	WELCOME: 0,
	CREATE: 1,
	MODE: 2,
	NAME: 3,
	INVITE: 4,
	LAUNCH: 5,
	FINISH: 6,
} as const;

export const CANMEET_SCHEDULED_STEP = {
	WELCOME: 0,
	CREATE: 1,
	MODE: 2,
	NAME: 3,
	DATE: 4,
	TIME: 5,
	DURATION: 6,
	RECURRING: 7,
	INVITE: 8,
	SCHEDULE: 9,
	FINISH: 10,
} as const;

// The Create Meeting form is a centered MUI Dialog that can scroll; re-measure
// as it mutates/resizes so the popover stays anchored.
const meetDialogObservers = {
	mutationObservables: ['.MuiDialog-paper'],
	resizeObservables: ['.MuiDialog-paper'],
};

const instantSteps: StepType[] = [
	{
		selector: 'body',
		position: 'center',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				title="Welcome to CANmeet 📹"
				body="Let’s start an instant meeting — up and running in a few clicks."
			/>
		),
	},
	{
		selector: '[data-tour="canmeet-create-btn"]',
		position: 'bottom',
		content: (
			<TourStepContent
				awaitAction
				tailPosition="bottom"
				body="Click “+ Create Meeting” to open the form."
			/>
		),
	},
	{
		selector: '[data-tour="canmeet-mode-toggle"]',
		position: 'left',
		content: (
			<TourStepContent
				hideBack
				awaitAction
				tailPosition="left"
				body="“Instant” starts a meeting right now. (“Schedule” is for setting one up later.)"
			/>
		),
		...meetDialogObservers,
	},
	{
		selector: '[data-tour="canmeet-name"]',
		position: 'left',
		content: (
			<TourStepContent
				tailPosition="left"
				requires="canmeet-name"
				hideBack
				body="Give your meeting a name to continue."
			/>
		),
		...meetDialogObservers,
	},
	{
		// Info-only: blocked so toggling it (which reveals the mail-list /
		// attachment fields and reflows the dialog) can't break the flow.
		selector: '[data-tour="canmeet-invite"]',
		position: 'left',
		styles: blockInteraction,
		// Hug just the toggle — the default mask padding bleeds down onto the
		// "Anyone with the link…" helper text that sits right below it.
		padding: { mask: 2 },
		content: (
			<TourStepContent
				tailPosition="left"
				body="You can also send invites for the meeting to a mailing list from here — we’ll leave that off for now."
			/>
		),
		...meetDialogObservers,
	},
	{
		selector: '[data-tour="canmeet-submit"]',
		position: 'left',
		content: (
			<TourStepContent
				awaitAction
				tailPosition="left"
				body="When you’re ready, click “Launch Meeting” to start now!"
			/>
		),
		...meetDialogObservers,
	},
	{
		// FINISH — reached after the sandboxed "launch" closes the dialog. No real
		// meeting is created during the tour.
		selector: 'body',
		position: 'center',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				finalStep
				title="That’s a meeting! 🎉"
				body="That’s how you start an instant meeting in CANmeet. This was just a walkthrough but go ahead and start your real first meeting!"
			/>
		),
	},
];

const scheduledSteps: StepType[] = [
	{
		selector: 'body',
		position: 'center',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				title="Welcome to CANmeet 📅"
				body="Let’s schedule a meeting for later — pick a time, invite people, done."
			/>
		),
	},
	{
		selector: '[data-tour="canmeet-create-btn"]',
		position: 'bottom',
		content: (
			<TourStepContent
				awaitAction
				tailPosition="bottom"
				body="Click “+ Create Meeting” to open the form."
			/>
		),
	},
	{
		selector: '[data-tour="canmeet-mode-toggle"]',
		position: 'left',
		content: (
			<TourStepContent
				awaitAction
				tailPosition="left"
				body="Click “Schedule” to set this meeting up for a later date."
			/>
		),
		...meetDialogObservers,
	},
	{
		selector: '[data-tour="canmeet-name"]',
		position: 'left',
		content: (
			<TourStepContent
				tailPosition="left"
				requires="canmeet-name"
				hideBack
				body="Give your meeting a name to continue."
			/>
		),
		...meetDialogObservers,
	},
	{
		selector: '[data-tour="canmeet-date"]',
		position: 'left',
		content: (
			<TourStepContent
				tailPosition="left"
				requires="canmeet-date"
				body="Pick the meeting date to continue."
			/>
		),
		...meetDialogObservers,
	},
	{
		selector: '[data-tour="canmeet-start-time"]',
		position: 'left',
		content: (
			<TourStepContent
				tailPosition="left"
				requires="canmeet-start-time"
				body="Set the start time to continue."
			/>
		),
		...meetDialogObservers,
	},
	{
		selector: '[data-tour="canmeet-duration"]',
		position: 'left',
		content: (
			<TourStepContent
				tailPosition="left"
				body="Choose how long the meeting should run."
			/>
		),
		...meetDialogObservers,
	},
	{
		selector: '[data-tour="canmeet-recurring"]',
		position: 'left',
		styles: blockInteraction,
		// No dialog observers here: toggling "Recurring" reflows the dialog, and a
		// re-measure on this step mis-places the popover. useCanmeetTourSync instead
		// advances to the RECURRENCE step on the rising edge of the toggle.
		content: (
			<TourStepContent
				tailPosition="left"
				body="Optionally you are able to turn on “Recurring” to repeat meetings on a schedule."
			/>
		),
	},
	{
		// Info-only: blocked so toggling it (which reveals the mail-list /
		// attachment fields and reflows the dialog) can't break the flow.
		selector: '[data-tour="canmeet-invite"]',
		position: 'left',
		styles: blockInteraction,
		// Hug just the toggle — the default mask padding bleeds down onto the
		// "Anyone with the link…" helper text that sits right below it.
		padding: { mask: 2 },
		content: (
			<TourStepContent
				tailPosition="left"
				body="You can also invite people from a mailing list here — we’ll leave that off for now."
			/>
		),
		...meetDialogObservers,
	},
	{
		selector: '[data-tour="canmeet-submit"]',
		position: 'left',
		content: (
			<TourStepContent
				awaitAction
				tailPosition="left"
				body="That’s it — click “Schedule Meeting” to save it!"
			/>
		),
		...meetDialogObservers,
	},
	{
		// FINISH — reached after the sandboxed "schedule" closes the dialog. No real
		// meeting is created during the tour.
		selector: 'body',
		position: 'center',
		styles: blockAllNoDim,
		content: (
			<TourStepContent
				finalStep
				title="Meeting scheduled! 🎉"
				body="That’s how you schedule a meeting in CANmeet. This was just a walkthrough but go ahead and schedule your real first meeting!"
			/>
		),
	},
];

export const canmeetInstantTour: TourConfig = {
	steps: instantSteps,
	startRoute: '/meet',
	allowedRoutes: ['/meet'],
};

export const canmeetScheduledTour: TourConfig = {
	steps: scheduledSteps,
	startRoute: '/meet',
	allowedRoutes: ['/meet'],
};
